/**
 * Organization API utilities
 * Handles organization context and membership data
 */

import { OrganizationInfo } from '../types/profile';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Fetch all organizations the current user belongs to
 * Supports future multi-organization scenarios
 */
export async function getUserOrganizations(): Promise<{
  success: boolean;
  data: OrganizationInfo[];
  error?: { message: string; type: string; code: string };
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/organizations`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch organizations');
    }

    // Transform the response to match OrganizationInfo interface
    const organizations: OrganizationInfo[] = (data.data?.organizations || []).map((org: Record<string, unknown>) => ({
      id: org.id,
      name: org.name,
      slug: org.slug,
      status: org.status || 'active',
      plan: org.plan || 'trial',
      membershipDate: org.createdAt || new Date().toISOString(),
      userRole: 'member', // Default role, will be updated by profile API
      companyEmail: org.companyEmail,
      settings: org.settings || {}
    }));

    return {
      success: true,
      data: organizations
    };
  } catch (error) {
    console.error('Error fetching user organizations:', error);
    return {
      success: false,
      data: [],
      error: {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        type: 'NETWORK_ERROR',
        code: 'ORGANIZATIONS_FETCH_ERROR'
      }
    };
  }
}

/**
 * Get organization display information
 * Formats organization data for UI display
 */
export function getOrganizationDisplayInfo(organization: OrganizationInfo) {
  // Map role to display label
  const getRoleLabel = (role: string): string => {
    const roleLower = role.toLowerCase();
    switch (roleLower) {
      case 'superadmin':
        return 'Super Admin';
      case 'admin':
        return 'Administrator';
      case 'member':
        return 'Member';
      case 'owner':
        return 'Owner';
      default:
        return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };
  
  return {
    displayName: organization.name,
    statusLabel: organization.status.charAt(0).toUpperCase() + organization.status.slice(1),
    planLabel: organization.plan.charAt(0).toUpperCase() + organization.plan.slice(1),
    roleLabel: getRoleLabel(organization.userRole),
    memberSince: new Date(organization.membershipDate).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    }),
    isActive: organization.status === 'active',
    isPremiumPlan: ['pro', 'premium', 'enterprise'].includes(organization.plan.toLowerCase())
  };
}

/**
 * Check if user has admin privileges in any organization
 */
export function hasAdminPrivileges(organizations: OrganizationInfo[]): boolean {
  return organizations.some(org => 
    org.userRole === 'admin' && org.status === 'active'
  );
}

/**
 * Get primary organization (first active organization)
 * For future multi-org support
 */
export function getPrimaryOrganization(organizations: OrganizationInfo[]): OrganizationInfo | null {
  const activeOrgs = organizations.filter(org => org.status === 'active');
  return activeOrgs.length > 0 ? activeOrgs[0] : null;
}

/**
 * Format organization membership duration
 */
export function getOrganizationMembershipDuration(membershipDate: string): string {
  const now = new Date();
  const joined = new Date(membershipDate);
  const diffTime = Math.abs(now.getTime() - joined.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months === 1 ? '' : 's'}`;
  } else {
    const years = Math.floor(diffDays / 365);
    const remainingMonths = Math.floor((diffDays % 365) / 30);
    if (remainingMonths === 0) {
      return `${years} year${years === 1 ? '' : 's'}`;
    }
    return `${years} year${years === 1 ? '' : 's'}, ${remainingMonths} month${remainingMonths === 1 ? '' : 's'}`;
  }
}

/**
 * Get a specific organization by ID
 * For compatibility with existing OrganizationContext
 */
export async function getOrganization(organizationId: string): Promise<{
  success: boolean;
  data?: OrganizationInfo;
  error?: { message: string; type: string; code: string };
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/organizations/${organizationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch organization');
    }

    // Transform the response to match OrganizationInfo interface
    const organization: OrganizationInfo = {
      id: data.data.id,
      name: data.data.name,
      slug: data.data.slug,
      status: data.data.status || 'active',
      plan: data.data.plan || 'trial',
      membershipDate: data.data.createdAt || new Date().toISOString(),
      userRole: data.data.userRole || 'member',
      companyEmail: data.data.companyEmail,
      settings: data.data.settings || {}
    };

    return {
      success: true,
      data: organization
    };
  } catch (error) {
    console.error('Error fetching organization:', error);
    return {
      success: false,
      error: {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        type: 'NETWORK_ERROR',
        code: 'ORGANIZATION_FETCH_ERROR'
      }
    };
  }
}

/**
 * Update organization information
 * For compatibility with existing OrganizationSettings component
 */
export async function updateOrganization(organizationId: string, updates: Partial<OrganizationInfo>): Promise<{
  success: boolean;
  data?: OrganizationInfo;
  error?: { message: string; type: string; code: string };
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/organizations/${organizationId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to update organization');
    }

    return {
      success: true,
      data: data.data
    };
  } catch (error) {
    console.error('Error updating organization:', error);
    return {
      success: false,
      error: {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        type: 'NETWORK_ERROR',
        code: 'ORGANIZATION_UPDATE_ERROR'
      }
    };
  }
}

/**
 * Update user profile
 * For compatibility with existing OrganizationSettings component
 * This is a wrapper around the profile API
 */
export async function updateUserProfile(updates: Record<string, unknown>): Promise<{
  success: boolean;
  data?: Record<string, unknown>;
  error?: { message: string; type: string; code: string };
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to update user profile');
    }

    return {
      success: true,
      data: data.data
    };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return {
      success: false,
      error: {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        type: 'NETWORK_ERROR',
        code: 'PROFILE_UPDATE_ERROR'
      }
    };
  }
}
